import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Send } from "lucide-react";
import { API, api, apiError, inr, shortDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const BUCKETS = ["current", "1-30", "31-60", "61-90", "90+"];
const LABEL = { current: "Not due", "1-30": "1-30d", "31-60": "31-60d", "61-90": "61-90d",
                "90+": "90d+" };

/** One-page ledger for a customer: opening balance, invoices, payments, running balance. */
export function StatementDialog({ customer, onClose }) {
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });
  const [channel, setChannel] = useState("email");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!customer?.id) return;
    setData(null);
    try {
      const q = new URLSearchParams();
      if (range.from) q.set("date_from", range.from);
      if (range.to) q.set("date_to", range.to);
      const { data: d } = await api.get(
        `/customers/${customer.id}/statement${q.toString() ? `?${q}` : ""}`);
      setData(d);
    } catch (e) {
      toast.error(apiError(e));
      onClose();
    }
  }, [customer, range, onClose]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post(`/customers/${customer.id}/statement/send`,
        { channel, date_from: range.from, date_to: range.to });
      if (res.delivered?.length) toast.success(`Statement sent by ${res.delivered.join(" & ")}`);
      else toast.info("Statement not delivered automatically — opening WhatsApp for you");
      if (res.whatsapp?.wa_link && channel !== "email")
        window.open(res.whatsapp.wa_link, "_blank", "noopener");
      if (res.email?.status === "no_address") toast.warning("This customer has no email saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  const pdfUrl = () => {
    const q = new URLSearchParams();
    if (range.from) q.set("date_from", range.from);
    if (range.to) q.set("date_to", range.to);
    return `${API}/customers/${customer.id}/statement.pdf${q.toString() ? `?${q}` : ""}`;
  };

  return (
    <Dialog open={!!customer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-4xl"
        data-testid="statement-dialog">
        <DialogHeader>
          <DialogTitle>Statement of account — {customer?.name}</DialogTitle>
          <DialogDescription>
            Every invoice and payment with a running balance. Leave the dates blank for the full
            history.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-2">
          <div>
            <div className="overline">From</div>
            <Input type="date" className="w-[150px]" data-testid="statement-from-input"
              value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
          </div>
          <div>
            <div className="overline">To</div>
            <Input type="date" className="w-[150px]" data-testid="statement-to-input"
              value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
          </div>
          <Button variant="outline" data-testid="statement-fy-button"
            onClick={() => setRange({ from: `${new Date().getMonth() < 3
              ? new Date().getFullYear() - 1 : new Date().getFullYear()}-04-01`, to: "" })}>
            This financial year
          </Button>
          <Button variant="ghost" data-testid="statement-alltime-button"
            onClick={() => setRange({ from: "", to: "" })}>
            All time
          </Button>
        </div>

        {!data ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-4" data-testid="statement-summary">
              {[
                ["Total billed", inr(data.totals.billed)],
                ["Received", inr(data.totals.received)],
                ["Balance due", inr(data.closing_balance)],
                ["Last payment", data.last_payment_date
                  ? shortDate(data.last_payment_date) : "none"],
              ].map(([k, v]) => (
                <div key={k} className="surface p-3">
                  <div className="overline">{k}</div>
                  <div className="font-mono-num text-base font-semibold">{v}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-1.5" data-testid="statement-ageing">
              {BUCKETS.map((b) => (
                <Badge key={b} variant={data.ageing[b] > 0 ? "secondary" : "outline"}
                  className="text-[10px]">
                  {LABEL[b]}: {inr(data.ageing[b])}
                </Badge>
              ))}
            </div>

            <div className="surface overflow-x-auto" data-testid="statement-ledger">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Particulars</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="text-xs" />
                    <TableCell className="text-xs font-medium">Opening balance</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell className="text-right font-mono-num text-xs">
                      {inr(data.opening_balance)}
                    </TableCell>
                  </TableRow>
                  {data.rows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs">{shortDate(r.date)}</TableCell>
                      <TableCell className="text-xs">{r.particulars}</TableCell>
                      <TableCell className="text-right font-mono-num text-xs">
                        {r.debit ? inr(r.debit) : ""}
                      </TableCell>
                      <TableCell className="text-right font-mono-num text-xs">
                        {r.credit ? inr(r.credit) : ""}
                      </TableCell>
                      <TableCell className="text-right font-mono-num text-xs">
                        {inr(r.balance)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell />
                    <TableCell className="text-xs font-medium">Closing balance due</TableCell>
                    <TableCell className="text-right font-mono-num text-xs">
                      {inr(data.totals.billed)}
                    </TableCell>
                    <TableCell className="text-right font-mono-num text-xs">
                      {inr(data.totals.received)}
                    </TableCell>
                    <TableCell className="text-right font-mono-num text-xs font-semibold">
                      {inr(data.closing_balance)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Select value={channel} onValueChange={setChannel}>
            <SelectTrigger className="w-[170px]" data-testid="statement-channel-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
              <SelectItem value="both">Email + WhatsApp</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" data-testid="statement-pdf-button"
            onClick={() => window.open(pdfUrl(), "_blank")}>
            <Download className="mr-1 h-4 w-4" /> PDF
          </Button>
          <Button onClick={send} disabled={sending || !data} data-testid="statement-send-button">
            {sending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              : <Send className="mr-1 h-4 w-4" />}
            Send statement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
